import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdOutlineInsights, MdRefresh } from 'react-icons/md'
import { fetchData } from '../../../helpers/fetchData'
import ReusableModal from '../../../shared/ReuseableModal'
import CustomButton from '../../../shared/CustomButton'
import CustomChip from '../../../shared/CustomChip'
import { Skeleton } from 'primereact/skeleton'

export const getJobProgress = job => {
  const explicitProgress = Number(job?.progress ?? job?.progress_percentage)
  if (Number.isFinite(explicitProgress) && explicitProgress >= 0) {
    return Math.min(100, explicitProgress)
  }
  const total = Number(job?.total_visits || 0)
  return total > 0
    ? Math.min(100, (Number(job?.processed_count || 0) / total) * 100)
    : 0
}

const jobStatusChipType = status => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return 'success'
    case 'completed_with_errors':
    case 'processing':
    case 'queued':
      return 'warning'
    case 'failed':
      return 'danger'
    default:
      return 'secondary'
  }
}

const normalizeText = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .trim()

// Reusable "Current Job Progress" modal: opens, fetches the current job once while
// showing a skeleton. Refresh is manual (button on top) with a 30s cooldown.
const JobProgressModal = ({
  isVisible,
  onClose,
  batchId,
  batchNumber = '',
  batchName = '',
  stepTitle = '',
  refreshKey = 0
}) => {
  const { t } = useTranslation()
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [cooldownLeft, setCooldownLeft] = useState(0)
  const cooldownTimer = useRef(null)

  const displayName = batchName || batchNumber

  const fetchJob = useCallback(
    async (showSpinner = true) => {
      if (!batchId) return
      if (showSpinner) setLoading(true)
      else setRefreshing(true)
      try {
        const response = await fetchData(
          'GET',
          `v3/claims/batches/${batchId}/current-job`,
          {}
        )
        setJob(response?.data || null)
      } catch (error) {
        console.error('Error fetching current batch job', error)
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    },
    [batchId]
  )

  useEffect(() => {
    if (isVisible && batchId) {
      fetchJob(true)
    } else {
      setJob(null)
      setLoading(false)
      setRefreshing(false)
      setCooldownLeft(0)
    }
  }, [isVisible, batchId, refreshKey])

  // Clean up cooldown timer on unmount.
  useEffect(() => () => clearInterval(cooldownTimer.current), [])

  const handleRefresh = () => {
    // Allow refresh only after the 30s cooldown has passed.
    if (cooldownLeft > 0 || refreshing) return
    setCooldownLeft(30)
    fetchJob(false)
  }

  useEffect(() => {
    if (!isVisible || cooldownLeft <= 0) return
    cooldownTimer.current = setInterval(() => {
      setCooldownLeft(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimer.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(cooldownTimer.current)
  }, [isVisible, cooldownLeft > 0])

  const percent = getJobProgress(job)
  const successCount = Number(job?.success_count || 0)
  const failedCount = Number(job?.failed_count || 0)
  const processedCount = Math.max(Number(job?.processed_count || 0), successCount + failedCount)
  const successPct = processedCount ? (successCount / processedCount) * 100 : 0
  const failedPct = processedCount ? (failedCount / processedCount) * 100 : 0

  return (
    <ReusableModal
      isVisible={isVisible}
      onClose={() => !loading && onClose?.()}
      title={t('Current Job Progress')}
      HeaderIcon={MdOutlineInsights}
      position='top'
      className='w-[95vw] md:w-[640px]'
    >
      {loading ? (
        <div className='space-y-3 py-2'>
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
            {[1, 2, 3].map(item => (
              <Skeleton key={item} height='64px' />
            ))}
          </div>
          <Skeleton width='100%' height='48px' />
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
            {[1, 2, 3, 4].map(item => (
              <Skeleton key={item} height='56px' />
            ))}
          </div>
        </div>
      ) : !job ? (
        <div className='py-8 text-center text-sm text-gray-500'>
          {t('No current job found for this batch')}
        </div>
      ) : (
        <div className='space-y-3'>
          {(stepTitle || displayName) && (
            <div className='flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
              <div>
                {stepTitle && <span className='font-semibold'>{stepTitle}</span>}
                {stepTitle && displayName && <span> | </span>}
                {displayName && (
                  <span>
                    {t('Batch No')}: <span className='font-semibold'>{displayName}</span>
                  </span>
                )}
              </div>
              <CustomButton
                label={cooldownLeft > 0 ? t('Wait') + ` ${cooldownLeft}s` : t('Refresh')}
                buttonType='primary'
                icon={<MdRefresh size={14} />}
                className='!h-[30px]'
                loading={refreshing}
                showLoadingText={false}
                disabled={cooldownLeft > 0}
                onClick={handleRefresh}
              />
            </div>
          )}

          <div className='flex flex-wrap items-center justify-between gap-2'>
            <div className='flex flex-wrap gap-2 text-sm'>
              <div className='rounded-md border border-gray-200 px-2.5 py-1.5'>
                <div className='text-xs text-gray-500'>{t('Job Type')}</div>
                <div className='font-semibold'>
                  {normalizeText(job?.batch_step || job?.job_type) || '-'}
                </div>
              </div>
              <div className='rounded-md border border-gray-200 px-2.5 py-1.5'>
                <div className='text-xs text-gray-500'>{t('Job ID')}</div>
                <div className='font-mono text-xs font-semibold'>{job?.job_id || '-'}</div>
              </div>
            </div>
            <CustomChip chipType={jobStatusChipType(job?.status)} label={normalizeText(job?.status) || '-'} className='h-[22px]' />
          </div>

          <div className='rounded-md border border-gray-200 p-3'>
            <div className='mb-2 flex items-center justify-between text-sm'>
              <span className='font-medium'>{t('Progress')}</span>
              <span className='font-semibold'>{percent.toFixed(2)}%</span>
            </div>
            <div className='h-2 overflow-hidden rounded-full bg-gray-100'>
              {failedCount > 0 && processedCount > 0 ? (
                <div
                  className='flex h-full overflow-hidden rounded-full'
                  style={{ width: `${Math.min(100, percent)}%` }}
                >
                  <div className='h-full bg-green-600' style={{ width: `${successPct}%` }} />
                  <div className='h-full bg-red-500' style={{ width: `${failedPct}%` }} />
                </div>
              ) : (
                <div
                  className='h-full rounded-full bg-success transition-all'
                  style={{ width: `${percent}%` }}
                />
              )}
            </div>
          </div>

          <div className='grid grid-cols-2 gap-2 text-sm sm:grid-cols-4'>
            <div className='rounded-md border border-gray-200 p-2.5'>
              <div className='text-xs text-gray-500'>{t('Processed')}</div>
              <div className='mt-1 font-semibold'>
                {job?.processed_count || 0} / {job?.total_visits || 0}
              </div>
            </div>
            <div className='rounded-md border border-green-200 bg-green-50 p-2.5'>
              <div className='text-xs text-green-700'>{t('Success')}</div>
              <div className='mt-1 font-semibold text-green-800'>{successCount || 0}</div>
            </div>
            <div className='rounded-md border border-amber-200 bg-amber-50 p-2.5'>
              <div className='text-xs text-amber-700'>{t('Skipped')}</div>
              <div className='mt-1 font-semibold text-amber-800'>{job?.skipped_count || 0}</div>
            </div>
            <div className='rounded-md border border-red-200 bg-red-50 p-2.5'>
              <div className='text-xs text-red-700'>{t('Failed')}</div>
              <div className='mt-1 font-semibold text-red-800'>{failedCount || 0}</div>
            </div>
          </div>
        </div>
      )}
    </ReusableModal>
  )
}

export default JobProgressModal
